<script>
import { mapState, mapActions } from 'vuex';
/* components */
import ModalConfirm from '@/components/modal/ModalConfirm';
import ModalEnterCode from '@/components/modal/ModalEnterCode';
import User from '@/components/User';
import UserBio from '@/components/UserBio';
import ChangePassword from '@/components/ChangePassword';
import GamesList from '@/components/GamesList';
import Button from '@/components/Button';
import InputBlock from '@/components/InputBlock';
import DropdownMultiple from '@/components/DropdownMultiple';
/* tools */
import { applyLanguage } from '@/scripts/language';
import { getAsset } from '@/scripts/utils';
import { getErrorCodes } from '@/scripts/utils/collection';
import { setProfile, getProfile, changeEmail } from '@/scripts/api/account';
import {
  initGoogleReCaptcha,
  processGoogleReCaptcha,
  setSaveState,
  updateUser,
  checkEmailValid,
  setAsyncInputError,
  emitClick,
} from '@/scripts/methods';
import { confirmEmail } from '@/scripts/api/account';
import { getAllConnections } from '@/scripts/api/connections';
import { catchError } from '@/scripts/utils/store-dependent';
import { email } from '@/scripts/watch';
import { wsConnection } from '@/scripts/modules/ws';
import { deleteConnection } from '@/scripts/api/connections';

export default {
  name: 'Settings',
  components: {
    Button,
    ChangePassword,
    GamesList,
    ModalConfirm,
    ModalEnterCode,
    User,
    UserBio,
    InputBlock,
    DropdownMultiple,
  },
  data() {
    return {
      email: '',
      /*  */
      country: null,

      /*  */
      routeLeave: null,
      saveState: null,
      /*  */
      emailConfirmationCode: '',
      /*  */
      codeError: '',
      emailError: null,
      validFlagId: null,
      connections: null,
      userDataPayload: null,
    };
  },
  watch: {
    email,
  },
  computed: {
    ...mapState([
      'user',
      'games',
      'error',
      'showModalConfirm',
      'showModalEnterCode',
    ]),
    changeEmailMode() {
      const { $route: route } = this;
      const result = route.params.option === 'email';

      return result;
    },
    changePasswordMode() {
      const { $route: route } = this;
      const result = route.params.option === 'password';

      return result;
    },
    isEmailValid() {
      const result = this.checkEmailValid();

      return result;
    },
    activeConnections() {
      return this.separateConnections(true);
    },
    passiveConnections() {
      return this.separateConnections();
    },
    dropdownMultipleItems() {
      const { user } = this;
      const { excludeFromLeaderboards } = user;

      const result = [
        {
          id: 'excludeFromLeaderboards',
          title: 'Hide my stats on the leaderboard',
          subtitle:
            'If you choose to hide your stats on the leaderboard, you may miss out on future bonuses and free tournament entries.',
          checked: excludeFromLeaderboards,
        },
      ];

      return result;
    },
  },
  created() {
    getAllConnections().then(connections => {
      this.connections = connections;
    });

    /* ws */
    wsConnection.setCallback(
      'gameConnection.statusUpdate',
      this.updateConnections
    );
  },
  mounted() {
    this.initGoogleReCaptcha().then(this.processGoogleReCaptcha);
  },
  /* beforeRouteLeave(to, from, next) {
    const { userDataPayload } = this;

    if (userDataPayload) {
      this.toggleModalConfirm(true);
      this.userDataPayload = false;
      this.routeLeave = to;
      next(false);
    } else {
      next();
    }
  }, */
  destroyed() {
    wsConnection.removeCallback('gameConnection.statusUpdate');
  },
  methods: {
    ...mapActions({
      setUser: 'setUser',
      toggleModalConfirm: 'toggleModalConfirm',
      toggleModalEnterCode: 'toggleModalEnterCode',
    }),
    separateConnections(active = false) {
      const { connections } = this;
      const result = {};
      const targetStatuses = ['connected', 'pending', 'failed'];

      if (!connections) return null;

      /* > */
      Object.entries(connections).filter(([id, setup]) => {
        const someStatusMatch = setup.some(({ status }) =>
          targetStatuses.includes(status)
        );

        const separator = someStatusMatch === active;

        if (separator) result[id] = setup;
      });
      /* < */

      if (!Object.keys(result).length) return null;

      return result;
    },
    save() {
      const { userDataPayload } = this;

      this.setSaveState('saving');

      setProfile(userDataPayload)
        .then(() => getProfile())
        .then(user => {
          this.setUser(user);
          this.userDataPayload = null;
          this.setSaveState('saved');
        })
        .catch(this.setSaveState);
    },
    onRecaptchaVerified(reCaptcha, id) {
      const { email } = this;
      const payload = { email, reCaptcha, operation: 'emailChange' };

      window.grecaptcha.reset(id);

      confirmEmail(payload).then(() => {
        this.toggleModalEnterCode(true);
      });
    },
    leavePage() {
      const { routeLeave } = this;

      this.toggleModalConfirm();
      this.userDataPayload = null;

      this.$router.push(routeLeave);
    },
    updatePayload(data) {
      this.userDataPayload = { ...this.userDataPayload, ...data };
    },
    changeMail() {
      const { email, emailConfirmationCode } = this;
      const payload = { email, emailConfirmationCode };

      changeEmail(payload)
        .then(this.updateUser)
        .then(() => {
          this.email = '';
          this.toggleModalEnterCode();
          this.emailConfirmationCode = '';
          this.codeError = '';

          this.$router.push('/settings');
        })
        .catch(e => {
          const targetCode = 'INVALID_CONFIRMATION_CODE';
          const codes = getErrorCodes(e);
          const match = codes.includes(targetCode);

          if (match) {
            this.codeError = 'Please enter a valid code.';
          } else {
            catchError(e);
          }
        });
    },
    setCode(val) {
      this.emailConfirmationCode = val;
      this.codeError = '';
    },
    updateConnections() {
      getAllConnections().then(connections => {
        this.connections = connections;
      });
    },
    preDisconnect(id) {
      this.connectionToDisconnectId = id;

      this.toggleModalConfirm(true);
    },
    disconnectAction() {
      this.toggleModalConfirm();

      return deleteConnection(this.connectionToDisconnectId).then(() => {
        this.connectionToDisconnectId = null;
        this.updateConnections();
      });
    },
    emitClick,
    initGoogleReCaptcha,
    processGoogleReCaptcha,
    applyLanguage,
    getAsset,
    setSaveState,
    updateUser,
    checkEmailValid,
    setAsyncInputError,
  },
};
</script>

<template>
  <div class="settings" v-cloak>
    <!-- changeEmail -->
    <div
      class="settings__content"
      v-show="changeEmailMode && !showModalEnterCode"
    >
      <div class="settings__title">
        <!-- back -->
        <router-link to="/settings" class="settings__arrow">
          <img :src="require('@/assets/svg/arrow-left.svg')" />
        </router-link>
        <span>Change Email</span>
      </div>

      <div class="settings__form">
        <div class="form">
          <!-- new  -->
          <div class="form__row">
            <InputBlock
              :error="emailError"
              :forceValue="email"
              @input="val => (email = val)"
              type="text"
            >
              <template #label>
                New Email
              </template>
            </InputBlock>
          </div>
          <!-- // -->

          <!-- save -->
          <div class="form__row form__row--btn-right">
            <Button
              ref="recaptcha-change-email"
              :disabled="!isEmailValid"
              class="btn"
            >
              Change Email
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- modal enter code -->
    <ModalEnterCode
      v-if="changeEmailMode && showModalEnterCode && !error"
      :email="email"
      :codeError="codeError"
      @close="toggleModalEnterCode()"
      @change="setCode"
      @action="changeMail"
      @retry="emitClick('recaptcha-change-email')"
    />

    <!-- change Password -->
    <ChangePassword v-else-if="changePasswordMode" />

    <!-- settings root -->
    <div class="settings__content" v-else-if="!changeEmailMode">
      <h1 class="settings__title">{{ applyLanguage('settings', 'Menu') }}</h1>

      <div class="settings__subtitle">Personal Information</div>

      <!-- user -->
      <div class="settings__user">
        <User v-if="user" :user="user" :in-settings="true" />
      </div>
      <!-- // -->

      <!-- form -->
      <div v-if="user" class="settings__form">
        <div class="form">
          <!-- firstName -->
          <div class="form__row">
            <InputBlock
              :forceValue="user.firstName"
              @input="val => updatePayload({ firstName: val })"
              placeholder="First name…"
            >
              <template #label>
                <span class="label">First name</span>
              </template>
            </InputBlock>
          </div>

          <!-- lastName -->
          <div class="form__row">
            <InputBlock
              :forceValue="user.lastName"
              @input="val => updatePayload({ lastName: val })"
              placeholder="Last name…"
            >
              <template #label>
                <span class="label">Last name</span>
              </template>
            </InputBlock>
          </div>

          <!-- email -->
          <div class="form__row">
            <InputBlock :forceValue="user.email" class="disabled-points">
              <template #label>
                <span class="label">Email</span>
              </template>
              <template #linkTop>
                <router-link to="/settings/email" class="link"
                  >Change Email</router-link
                >
              </template>
            </InputBlock>
          </div>
          <!-- // -->

          <!-- fake password -->
          <div class="form__row">
            <InputBlock
              class="disabled-points"
              placeholder="******************"
              type="password"
            >
              <template #label>
                <span class="label">Password</span>
              </template>
              <template #linkTop>
                <router-link
                  v-if="user.hasPassword"
                  to="/settings/password"
                  class="link"
                  >Change Password</router-link
                >
              </template>
            </InputBlock>
          </div>
          <!-- // -->

          <!-- UserBio -->
          <div class="form__row">
            <UserBio :user="user" @change="updatePayload" />
          </div>
          <!-- // -->

          <!-- Privacy -->
          <div class="form__row form__row--privacy">
            <DropdownMultiple
              title="Privacy Settings"
              :items="dropdownMultipleItems"
              @change="updatePayload"
            />
          </div>
          <!--  -->

          <!-- save -->
          <div class="form__row form__row--btn-right">
            <div class="saved">
              <div class="saved__result" v-show="saveState === 'saved'">
                <div class="saved__img">
                  <img :src="require('@/assets/svg/checked-symbol.svg')" />
                </div>
                <div class="saved__text">changes were saved</div>
              </div>
              <div class="saved__btn">
                <Button
                  class="orange"
                  :disabled="saveState === 'saving' || !userDataPayload"
                  @click="save"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- // -->

      <!-- games -->
      <div class="settings__games">
        <div class="settings__subtitle">Personal Information</div>
        <div v-if="activeConnections" class="settings__games__block">
          <div class="settings__games__title settings__games__title--connected">
            My connected games
          </div>
          <GamesList
            :connections="activeConnections"
            @disconnect="preDisconnect"
          />
        </div>
        <div v-if="passiveConnections" class="settings__games__block">
          <div class="settings__games__title settings__games__title--connect">
            Connect a game
          </div>
          <GamesList :connections="passiveConnections" />
        </div>
      </div>
      <!-- // -->
    </div>

    <!-- modal confirm -->
    <ModalConfirm
      v-if="showModalConfirm"
      @close="() => toggleModalConfirm()"
      @confirm="disconnectAction"
      title="If you disconnect your account you will lose all of your data and stats for this game"
      color="negative"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables.scss';
.settings {
  &__content {
    max-width: 600px;
    width: 100%;
    padding: 40px 30px 50px 0;
  }

  &__title {
    font-family: Poppins-Light;
    font-size: 36px;
    line-height: 51px;
    color: #ffffff;
    margin: 0 0 30px;
    display: flex;
    align-items: center;
  }

  &__games {
    &__block {
      & + & {
        margin-top: 40px;
      }
    }

    &__title {
      font-family: Poppins-Regular;
      font-size: 14px;
      line-height: 20px;
      margin-bottom: 30px;

      &--connected {
        color: #38d5b8;
      }

      &--connect {
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }

  &__user {
    margin-bottom: 30px;
  }

  &__arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 20px;
    cursor: pointer;
    transition: all 0.2;

    img {
      width: 16px;
      height: 14px;
      display: flex;
    }

    &:hover {
      opacity: 0.7;
    }
  }

  &__subtitle {
    font-family: Poppins-Regular;
    font-size: 16px;
    line-height: 23px;
    color: #ffffff;
    margin-bottom: 30px;
    margin-top: 50px;
  }
}

.form {
  width: 100%;

  &__row {
    width: 100%;

    & + & {
      margin-top: 30px;
    }

    & + &--btn,
    & + &--btn-right {
      margin-top: 50px;
    }

    &--btn-right {
      display: flex;
      justify-content: flex-end;
      width: 100%;
    }

    & + &--privacy {
      margin-top: 40px;
    }

    &--privacy + &--btn-right {
      margin-top: 30px;
    }
  }
}

.label {
  font-family: Poppins-Regular;
  font-size: 14px;
  line-height: 20px;
  color: rgba(255, 255, 255, 0.7);
}

.link {
  font-family: Poppins-Regular;
  font-size: 14px;
  line-height: 20px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: underline;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
}

.btn {
  height: 44px;
  border-radius: 17px;
  background-color: #ffc107;
  min-width: 170px;
  outline: none;
  padding: 5px 10px;
  font-family: Poppins-Medium;
  font-size: 14px;
  line-height: 20px;
  color: #0d0d0d;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  border: 0;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.5;
  }
}

.saved {
  display: flex;
  align-items: center;

  &__text {
    font-family: Poppins-Regular;
    font-size: 12px;
    line-height: 17px;
    color: #2fdabf;
  }

  &__result {
    display: flex;
    align-items: center;
    margin-right: 30px;
  }

  &__img {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 8px;
    height: 8px;
    margin-right: 6px;
    margin-top: 2px;

    img {
      display: flex;
      width: 100%;
      height: 100%;
    }
  }
}

@media (max-width: $mobile) {
  .settings {
    &__content {
      padding: 20px 15px 80px;
    }

    &__title {
      font-size: 21px;
      line-height: 29px;
    }

    &__user {
      margin-bottom: 41px;
    }

    &__games {
      margin-top: 40px;
      border-top: 1px solid rgba(255, 255, 255, 0.2);

      .settings__subtitle {
        margin-top: 40px;
      }
    }
  }

  .form {
    &__row {
      & + &--btn,
      & + &--btn-right {
        margin-top: 40px;
      }
    }
  }

  .link {
    font-size: 12px;
    line-height: 17px;
    color: #ffffff;
  }

  .label {
    font-size: 12px;
    line-height: 17px;
  }

  .saved {
    position: relative;

    &__result {
      position: absolute;
      right: 0;
      margin: 0;
      bottom: 100%;
      margin-bottom: 7px;
    }
  }
}
</style>
